import React, { useEffect, useMemo, useState, createContext } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import {
    leaderboardDataSelector,
    leaderboardNavSelector,
    leaderboardFilterSelector,
    leaderboardFavouriteSelector
} from 'common/store/modules/brownlow-tracker/leaderboard/selectors';
import {
    useGetBrownlowPredictorQuery,
    useGetBrownlowPredictorSearchQuery
} from 'common/store/modules/api/afl-api';
import { Table } from 'widgets/player-awards-tracker/js/components/Leaderboard/components/table/components/Table';
import { Loader } from 'common/react/components/Loader';
import { EmptyState } from 'common/react/components/EmptyState';
import {
    assignPositions,
    composeColumnData,
    composePredictorRowData
} from 'widgets/player-awards-tracker/js/components/Leaderboard/components/table/utils';
import {
    UPDATE_FAVOURITE_PLAYER,
    FETCH_LEADERBOARD_DATA
} from 'common/store/modules/brownlow-tracker/leaderboard/actions';
import {
    sortFavourites,
    sortPlayersByTotalVotesByNameByFirstName
} from '../utils/utils';
import { TooltipContent } from 'widgets/player-awards-tracker/js/components/Tooltip';
import { useGetTeamsQuery } from 'common/store/modules/api/afl-api';
import { useBettingVis } from 'common/react/hooks/use-betting-vis';
import { useGetBrownlowBettingOddsQuery } from 'common/store/modules/api/cfs-api';

import { usePagination } from 'widgets/player-awards-tracker/js/components/hooks/use-fetch-more-data';

const PAGE_SIZE = 20;

export const PredictorCurrentRoundContext = createContext('');

export const BrownlowPredictor = ({ seasonId }) => {
    const [data, setData] = useState([]);
    const [noDataAvailable, setNoDataAvailable] = useState(false);
    const [sortByFavourites, setSortByFavourites] = useState(false);
    const [hideLoadMoreBtn, setHideLoadMoreBtn] = useState(false);

    const favourites = useSelector(leaderboardFavouriteSelector);
    const leaderboardFilter = useSelector(leaderboardFilterSelector);

    const dispatch = useDispatch();
    const nav = useSelector(leaderboardNavSelector);

    const seasons = useSelector(
        (state) =>
            state.seasons[`competition_${nav.competition.id}`]?.list ?? []
    );
    const season = seasons.find((item) => item.id === nav.season.id);
    const seasonPrId = season?.providerId ?? '';
    const rounds = useSelector(
        (state) => state.rounds[`season_${nav.season.id}`]?.list ?? []
    );
    const { data: teamsData } = useGetTeamsQuery(nav.season.id, {
        skip: !nav.season.id ? true : false
    });

    const teams = teamsData || [];
    const team = teams.find((item) => item.id === nav.team.id);
    const teamId = team?.providerId ?? '';

    const teamFilter = nav.team.id;

    const [
        shownData,
        fetchMoreData,
        currentPage,
        setShownData,
        setCurrentPage
    ] = usePagination(data, PAGE_SIZE, teamId);

    const { data: bettingOddsData } = useGetBrownlowBettingOddsQuery(null, {
        pollingInterval: PULSE.app.common.CONSTANTS.TIME.ONE_MINUTE_IN_MS
    });
    const shouldShowBettingOdds =
        useBettingVis() &&
        bettingOddsData?.displayInApp === true &&
        bettingOddsData?.outcomes?.length > 0 &&
        season?.id === Number(seasonId);

    const teamIdFromFilters = teamFilter !== -1 ? teamFilter.toString() : '';

    const {
        data: players,
        isLoading,
        isFetching,
        isError: isPredictorError
    } = useGetBrownlowPredictorQuery(
        {
            compSeasonId: season ? season?.id : null,
            page: currentPage - 1,
            team: teamIdFromFilters
        },
        {
            skip: !(
                season?.id &&
                (leaderboardFilter.query === null ||
                    leaderboardFilter.query === '')
            )
        },
        { refetchOnMountOrArgChange: true }
    );

    const {
        data: searchPlayers,
        isLoading: isLoadingSearch,
        isFetching: isFetchingSearch,
        isError: isPlayerSearchError
    } = useGetBrownlowPredictorSearchQuery(
        {
            compSeasonId: season ? season?.id : null,
            term: leaderboardFilter.query
        },
        {
            skip:
                leaderboardFilter.query === null ||
                leaderboardFilter.query === ''
        }
    );

    /**
     * On Player Favourite Click
     * Modify favourites on player add/remove
     *
     * @param {*} playerId
     */
    const onPlayerFavouriteClick = (playerId) => {
        dispatch(
            UPDATE_FAVOURITE_PLAYER.request({
                id: null,
                providerId: playerId
            })
        );
    };

    /**
     * Set row data
     */
    useEffect(() => {
        setNoDataAvailable(false);

        if (
            !players ||
            (!players?.results?.length && !searchPlayers?.results?.length) ||
            isPlayerSearchError ||
            isPredictorError
        ) {
            if (!isPlayerSearchError) {
                setNoDataAvailable(true);
            }
            setShownData([]);
            return;
        }

        let searchPlayersSorted;
        if (leaderboardFilter.query && leaderboardFilter.query !== '') {
            if (searchPlayers && searchPlayers.results?.length) {
                searchPlayersSorted = {};
                searchPlayersSorted.results =
                    sortPlayersByTotalVotesByNameByFirstName(
                        searchPlayers.results
                    );

                if (searchPlayers.newItems?.length) {
                    searchPlayersSorted.newItems = searchPlayers.newItems;
                }
            } else {
                setShownData([]);
                return;
            }
        }

        const playersData =
            leaderboardFilter.query &&
            leaderboardFilter.query !== '' &&
            searchPlayersSorted
                ? searchPlayersSorted
                : players;

        setHideLoadMoreBtn(playersData?.newItems?.length <= PAGE_SIZE);

        let items =
            playersData?.results?.map((item) => {
                const roundByRoundVotes = Object.keys(item.rounds).map(
                    (key) => ({
                        matchId: '',
                        roundNumber: key,
                        votes: item.rounds[key]?.points ?? ''
                    })
                );

                const teamObject = teams.find(
                    (teamItem) => teamItem.id === item.teamId
                );

                return {
                    eligible: item.eligible ?? false,
                    player: {
                        ...item
                    },
                    roundByRoundVotes: roundByRoundVotes,
                    team: {
                        teamAbbr: teamObject?.abbreviation,
                        teamId: teamObject?.providerId,
                        teamName: teamObject?.name,
                        teamNickname: teamObject?.nickname
                    },
                    totalVotes: item.totalVotes ?? '',
                    bettingOdds: shouldShowBettingOdds
                        ? bettingOddsData?.outcomes?.find(
                              (outcome) =>
                                  item?.providerId === outcome?.playerId
                          )?.price ?? null
                        : null
                };
            }) ?? [];

        if (favourites.length && sortByFavourites) {
            items = sortFavourites(items, favourites, teamId);
        }

        items = items.map((item) => ({
            ...item
        }));

        setData(assignPositions(items));
    }, [
        favourites,
        sortByFavourites,
        teamId,
        bettingOddsData?.outcomes,
        shouldShowBettingOdds,
        seasonPrId,
        setShownData,
        teamsData,
        players,
        searchPlayers,
        isPlayerSearchError,
        isPredictorError,
        leaderboardFilter
    ]);

    // set the shown data based on the filtered results
    useEffect(() => {
        if (
            leaderboardFilter.query &&
            leaderboardFilter.query !== '' &&
            searchPlayers?.results?.length === shownData?.length
        ) {
            // hide pagination, if player filter in use and no data left
            setHideLoadMoreBtn(true);
        } else {
            // fetch more data, if player filter not in use
            setShownData(data.slice(0, PAGE_SIZE * currentPage));
        }
    }, [data, setShownData, currentPage]);

    useEffect(() => {
        dispatch(
            FETCH_LEADERBOARD_DATA.request({
                type: 'season',
                seasonPid: seasonPrId,
                teamId: teamId ?? ''
            })
        );
    }, [seasonPrId, teamId]);

    /**
     * Get columns
     */
    const columns = useMemo(() => composeColumnData(rounds), [rounds]);

    /**
     * Get rows
     */
    const rowData = useMemo(
        () =>
            shownData.map((item) => {
                const yearFromCompSeason = seasonPrId.replace('CD_S', '');
                const playerFromProviderId = item.player.providerId.replace(
                    'CD_I',
                    ''
                );
                let headshotUrl;

                if (yearFromCompSeason) {
                    headshotUrl = `https:${
                        PULSE.app.environment.playerImagePath
                    }/${'AFL'}/${yearFromCompSeason}/${playerFromProviderId}.png?im=Scale,width=0.6,height=0.6`;
                }

                item.player.photoURL = headshotUrl;

                return composePredictorRowData(
                    item,
                    columns.filter(
                        (col) =>
                            !['player', 'totalVotes', 'bettingOdds'].includes(
                                col.accessor
                            )
                    ),
                    rounds,
                    leaderboardFilter.query && leaderboardFilter.query !== ''
                        ? searchPlayers?.results
                        : players?.results
                );
            }),
        [
            columns,
            shownData,
            rounds,
            seasonPrId,
            players,
            searchPlayers,
            leaderboardFilter
        ]
    );

    // fetch and select leaderboard data

    const leaderboardData = useSelector((state) =>
        leaderboardDataSelector(state, seasonPrId)
    );

    useEffect(() => {
        dispatch(
            FETCH_LEADERBOARD_DATA.request({
                type: 'season',
                seasonPid: seasonPrId,
                teamId: teamId ?? ''
            })
        );
    }, [seasonPrId, teamId]);

    /**
     * Resets the current page when the team filter is being used
     * so that the new results start from the top.
     */
    useMemo(() => {
        setCurrentPage(1);
    }, [teamId]);

    // ===== Render ============================================== //

    if (isLoading || isLoadingSearch) {
        // Loader for waiting for data
        return (
            <div className="career-and-season-stats__loader">
                <Loader />
            </div>
        );
    } else if (noDataAvailable) {
        // Error handling for when there is no data
        return (
            <div className="career-and-season-stats__empty-state">
                <EmptyState
                    titleTranslation="label.brownlow.tracker.error.noDataAvailable"
                    summaryTranslation="label.brownlow.tracker.error.noDataAvailable.summary"
                />
            </div>
        );
    }

    const currentRoundNumber = season?.currentRoundNumber;

    return (
        <PredictorCurrentRoundContext.Provider value={currentRoundNumber}>
            {players?.results ? (
                <Table
                    columns={columns}
                    data={rowData}
                    fullData={data}
                    modifier={`brownlow-tracker-leaderboard brownlow-leaderboard stats-table--brownlow-predictor ${
                        shouldShowBettingOdds
                            ? 'stats-table--brownlow-tracker-leaderboard--show-odds'
                            : ''
                    }`}
                    update={fetchMoreData}
                    key={rowData[0]?.player.playerId}
                    seasonPid={seasonPrId}
                    favourites={favourites}
                    sortByFavourites={sortByFavourites}
                    setSortByFavourites={setSortByFavourites}
                    onPlayerFavouriteClick={onPlayerFavouriteClick}
                    filteredResults={leaderboardFilter?.query?.length > 0}
                    isBrownlowV2={true}
                    isFetching={isFetching || isFetchingSearch}
                    hideLoadMoreBtn={hideLoadMoreBtn}
                    primaryTooltipComponent={(row, cell) => (
                        <TooltipContent
                            row={row.original}
                            cell={cell}
                            player={row.original?.player.playerId}
                            seasonPid={seasonPrId}
                            leaderboardData={leaderboardData}
                            isPredictor={true}
                        />
                    )}
                />
            ) : null}
        </PredictorCurrentRoundContext.Provider>
    );
};

BrownlowPredictor.propTypes = {
    seasonId: PropTypes.string.isRequired
};
